import { useEffect, useRef } from "react";
import type { Location as RouterLocation } from "react-router-dom";
import { useLocation } from "react-router-dom";

const REPORT_ENDPOINT =
  typeof import.meta.env.VITE_API_URL === "string" && import.meta.env.VITE_API_URL.length > 0
    ? `${import.meta.env.VITE_API_URL}/security/csp-report`
    : undefined;

const buildReportPayload = (
  event: SecurityPolicyViolationEvent,
  location: RouterLocation
) => {
  const { pathname, search, hash } = location;
  const fullPath = `${pathname}${search}${hash}`;

  const report = {
    "document-uri": event.documentURI ?? "",
    referrer: event.referrer ?? "",
    "blocked-uri": event.blockedURI ?? "",
    "violated-directive": event.violatedDirective ?? "",
    "effective-directive": event.effectiveDirective ?? "",
    "original-policy": event.originalPolicy ?? "",
    disposition: event.disposition ?? "",
    "status-code": typeof event.statusCode === "number" ? event.statusCode : undefined,
    "script-sample": event.sample ?? "",
    "source-file": event.sourceFile ?? "",
    "line-number": typeof event.lineNumber === "number" ? event.lineNumber : undefined,
    "column-number": typeof event.columnNumber === "number" ? event.columnNumber : undefined,
  };

  const context = {
    pathname,
    search,
    hash,
    fullPath,
    location: window.location.href,
    title: document.title,
    timestamp: new Date().toISOString(),
  };

  return {
    source: "oak-react",
    reports: [report],
    context,
  };
};

const sendReport = (payload: unknown) => {
  if (!REPORT_ENDPOINT) {
    return;
  }

  const body = JSON.stringify(payload);

  if (navigator.sendBeacon) {
    const succeeded = navigator.sendBeacon(
      REPORT_ENDPOINT,
      new Blob([body], { type: "application/json" })
    );
    if (succeeded) {
      return;
    }
  }

  void fetch(REPORT_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    keepalive: true,
    body,
    mode: "cors",
  });
};

export function CspViolationReporter(): null {
  const location = useLocation();
  const locationRef = useRef(location);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    if (!REPORT_ENDPOINT) {
      return;
    }

    const handler = (event: SecurityPolicyViolationEvent) => {
      const payload = buildReportPayload(event, locationRef.current);
      sendReport(payload);
    };

    document.addEventListener("securitypolicyviolation", handler);
    return () => document.removeEventListener("securitypolicyviolation", handler);
  }, []);

  return null;
}
